// Lookbook generator + sender (internal staff + reps). Seed the product list from a collection
// (searchable) and/or search-add hand-picked products, reorder + deselect them, feed the copy,
// preview the exact PDF, download it, and send it to clients picked from a role-scoped list.
import { useEffect, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import {
  fetchLookbookCollections,
  fetchLookbookProducts,
  fetchCollectionProducts,
  fetchLookbookRecipients,
  previewLookbook,
  generateLookbook,
  sendLookbook,
  fetchLookbookSends,
  fetchLookbookContacts,
  rewriteLookbookCopy,
  draftLookbookMessage,
} from '../api/lookbooks.js';
import { storeShort, storeName } from '../lib/stores.js';
import { allowedStoreCodes } from '../lib/access.js';
import { useAuthStore } from '../stores/authStore.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const MAX_PRODUCTS = 60;

// `embedded` (inside Marketing): just the PDF builder — emailing a lookbook is done as a Marketing
// email with a lookbook block, so the old send box is hidden and past sends are shown read-only.
export default function Lookbooks({ embedded = false }) {
  // Stores this member may actually build from. With access to only one store there's no choice to
  // make, so the picker is hidden entirely rather than shown as a single dead button.
  const user = useAuthStore((s) => s.user);
  const storeOptions = allowedStoreCodes(user) || ['A', 'B'];
  const [store, setStore] = useState(storeOptions[0]);

  // Collection combobox (searchable)
  const [collections, setCollections] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const collectionBoxRef = useRef(null);

  // The single, ordered product list that becomes the lookbook.
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState([]); // [{ id, title, image }]

  // Product search (to add hand-picked products)
  const [products, setProducts] = useState([]);
  const [productQuery, setProductQuery] = useState('');
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [headline, setHeadline] = useState('');
  const [intro, setIntro] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  // Recipients (role-scoped client list)
  const [recipientsAll, setRecipientsAll] = useState([]);
  const [reps, setReps] = useState([]);
  const [repFilter, setRepFilter] = useState(''); // '' = all reps; a rep id filters to their clients
  const [recipientSel, setRecipientSel] = useState(() => new Set());
  const [recipientSearch, setRecipientSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [introMessage, setIntroMessage] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sends, setSends] = useState([]);

  const [source, setSource] = useState('');
  const [sourceFile, setSourceFile] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState('');
  const [contact, setContact] = useState({ name: '', title: '', phone: '', email: '' });

  const loadSends = () => fetchLookbookSends().then(setSends).catch(() => setSends([]));
  const clearPreview = () => setPreview(null);

  useEffect(() => {
    loadSends();
    fetchLookbookContacts().then(setContacts).catch(() => setContacts([]));
    fetchLookbookRecipients()
      .then((d) => {
        setRecipientsAll(d.recipients || []);
        setReps(d.reps || []);
      })
      .catch(() => {
        setRecipientsAll([]);
        setReps([]);
      });
  }, []);

  // Collections reload per store; the ordered list also resets (products are store-specific).
  useEffect(() => {
    setCollections([]);
    setCollectionSearch('');
    setSelected([]);
    setTitle('');
    setProducts([]);
    setCursor(null);
    setPreview(null);
    fetchLookbookCollections(store)
      .then((c) => setCollections(c.filter((x) => x.productsCount > 0)))
      .catch(() => setCollections([]));
  }, [store]);

  // Close the collection menu on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (collectionBoxRef.current && !collectionBoxRef.current.contains(e.target)) setCollectionOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filteredCollections = collections.filter((c) =>
    c.title.toLowerCase().includes(collectionSearch.trim().toLowerCase())
  );

  // Seed the ordered list from a collection (replaces the current selection).
  const loadCollection = async (c) => {
    setCollectionOpen(false);
    setCollectionSearch(c.title);
    setLoadingCollection(true);
    setError('');
    try {
      const { products: rows, title: collTitle } = await fetchCollectionProducts(store, c.id);
      setSelected(rows.slice(0, MAX_PRODUCTS).map((p) => ({ id: p.id, title: p.title, image: p.image })));
      setTitle((t) => t || collTitle || c.title);
      clearPreview();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoadingCollection(false);
    }
  };

  const loadProducts = async ({ reset }) => {
    setLoadingProducts(true);
    try {
      const { products: rows, pageInfo } = await fetchLookbookProducts({
        store,
        q: productQuery,
        cursor: reset ? null : cursor,
      });
      setProducts((prev) => (reset ? rows : [...prev, ...rows]));
      setCursor(pageInfo?.endCursor || null);
      setHasMore(Boolean(pageInfo?.hasNextPage));
    } catch {
      if (reset) setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(() => loadProducts({ reset: true }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, pickerOpen, productQuery]);

  const isSelected = (id) => selected.some((x) => x.id === id);
  const toggleProduct = (p) => {
    setSelected((prev) => {
      if (prev.some((x) => x.id === p.id)) return prev.filter((x) => x.id !== p.id);
      if (prev.length >= MAX_PRODUCTS) return prev;
      return [...prev, { id: p.id, title: p.title, image: p.image }];
    });
    clearPreview();
  };
  const removeSelected = (id) => {
    setSelected((prev) => prev.filter((x) => x.id !== id));
    clearPreview();
  };

  // Reorder — native drag + up/down fallback.
  const dragIndex = useRef(null);
  const onDrop = (to) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === to) return;
    setSelected((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    clearPreview();
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= selected.length) return;
    setSelected((prev) => {
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    clearPreview();
  };

  const hasContact = contact.name || contact.title || contact.phone || contact.email;
  const body = () => ({
    store,
    productIds: selected.map((s) => s.id),
    ...(title.trim() ? { title: title.trim() } : {}),
    headline,
    intro,
    ...(hasContact ? { contact } : {}),
  });

  const doRewrite = async () => {
    setBusy('rewrite');
    setError('');
    try {
      const r = await rewriteLookbookCopy({ source, collection: title, file: sourceFile });
      setHeadline(r.headline || '');
      setIntro(r.intro || '');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy('');
    }
  };

  const onContactPick = (id) => {
    setContactId(id);
    const c = contacts.find((x) => x.id === id);
    if (c) setContact((prev) => ({ ...prev, name: c.name || '', email: c.email || '' }));
  };

  const doPreview = async () => {
    setBusy('preview');
    setError('');
    try {
      const b = body();
      const [info, blob] = await Promise.all([previewLookbook(b), generateLookbook(b)]);
      setPreview(info);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy('');
    }
  };

  const doDownload = async () => {
    setBusy('download');
    setError('');
    try {
      const blob = await generateLookbook(body());
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy('');
    }
  };

  // Recipients — filter by search text and by assigned rep (a client can have several reps).
  const filteredRecipients = recipientsAll.filter((r) => {
    if (repFilter && !(r.repUserIds || []).includes(repFilter)) return false;
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return true;
    return `${r.name || ''} ${r.email} ${r.company || ''} ${(r.repNames || []).join(' ')}`.toLowerCase().includes(q);
  });
  const toggleRecipient = (email) =>
    setRecipientSel((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  const selectAllFiltered = () =>
    setRecipientSel((prev) => {
      const next = new Set(prev);
      filteredRecipients.forEach((r) => next.add(r.email));
      return next;
    });
  const clearRecipients = () => setRecipientSel(new Set());
  const recipientList = Array.from(recipientSel);

  const doDraftMessage = async () => {
    setBusy('draftMsg');
    setError('');
    try {
      const r = await draftLookbookMessage(body());
      if (r.subject) setSubject(r.subject);
      setIntroMessage(r.body || '');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy('');
    }
  };

  const doSend = async () => {
    setBusy('send');
    setError('');
    try {
      const res = await sendLookbook({ ...body(), recipients: recipientList, subject, introMessage });
      setSendResult(res);
      setConfirming(false);
      loadSends();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy('');
    }
  };

  const canGenerate = selected.length > 0;

  return (
    <>
      {!embedded && (
        <PageHeader title="Lookbooks" subtitle="Build a branded PDF lookbook from a collection or hand-picked products, reorder them, then send it to your clients." />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Builder */}
        <div className="space-y-4 rounded-lg border border-line bg-surface p-5 shadow-sm">
          {/* Store picker — only when this member can build from more than one. The two stores share
              ~98% of collection NAMES but their contents differ (e.g. "Best Sellers" is a different
              curation on each), so this genuinely changes what goes in the PDF — hence the label. */}
          {storeOptions.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-text-primary">Product source</label>
              <p className="mb-1.5 text-xs text-text-muted">
                Which store's collections to build from — {storeName('A')} and {storeName('B')} share most collections, but their contents can differ.
              </p>
              <div className="flex gap-2">
                {storeOptions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStore(s)}
                    className={`rounded-full px-3 py-1 text-sm ${store === s ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary'}`}
                  >
                    {storeShort(s)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Searchable collection loader */}
          <div ref={collectionBoxRef} className="relative">
            <label className="block text-sm font-medium text-text-primary">Load a collection</label>
            <p className="text-xs text-text-muted">Fills the product list below (you can then reorder, remove, or add more).</p>
            <input
              value={collectionSearch}
              onChange={(e) => {
                setCollectionSearch(e.target.value);
                setCollectionOpen(true);
              }}
              onFocus={() => setCollectionOpen(true)}
              placeholder={loadingCollection ? 'Loading collection…' : 'Search collections…'}
              className="mt-1.5 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
            {collectionOpen && filteredCollections.length > 0 && (
              <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-line bg-surface shadow-lg">
                {filteredCollections.map((c) => (
                  <button
                    key={c.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => loadCollection(c)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-raised"
                  >
                    {c.image ? (
                      <img src={c.image} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                    ) : (
                      <span className="h-8 w-8 shrink-0 rounded bg-surface-raised" />
                    )}
                    <span className="flex-1 truncate text-text-primary">{c.title}</span>
                    <span className="text-xs text-text-muted">{c.productsCount}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary">Lookbook title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Shown on the cover, e.g. Spring Edit"
              className="mt-1.5 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </div>

          {/* Ordered, draggable, removable product list */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-text-primary">Products in this lookbook</label>
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <span>
                  {selected.length}/{MAX_PRODUCTS}
                </span>
                {selected.length > 0 && (
                  <button onClick={() => { setSelected([]); clearPreview(); }} className="text-error hover:underline">
                    Clear
                  </button>
                )}
              </div>
            </div>
            {selected.length === 0 ? (
              <div className="mt-1.5 rounded-md border border-dashed border-line px-3 py-6 text-center text-xs text-text-muted">
                Load a collection above, or add products below. Drag to reorder — this is the order they appear.
              </div>
            ) : (
              <ul className="mt-1.5 space-y-1">
                {selected.map((s, i) => (
                  <li
                    key={s.id}
                    draggable
                    onDragStart={() => { dragIndex.current = i; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop(i)}
                    className="flex items-center gap-2 rounded-md border border-line bg-surface-raised/40 px-2 py-1.5"
                  >
                    <span className="cursor-grab select-none text-text-muted" title="Drag to reorder" aria-hidden>⋮⋮</span>
                    <span className="w-5 text-center text-xs text-text-muted">{i + 1}</span>
                    {s.image ? (
                      <img src={s.image} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
                    ) : (
                      <span className="h-9 w-9 shrink-0 rounded bg-surface-raised" />
                    )}
                    <span className="flex-1 truncate text-sm text-text-primary">{s.title}</span>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="rounded px-1 text-text-muted hover:bg-surface disabled:opacity-30" title="Move up">↑</button>
                      <button onClick={() => move(i, 1)} disabled={i === selected.length - 1} className="rounded px-1 text-text-muted hover:bg-surface disabled:opacity-30" title="Move down">↓</button>
                      <button onClick={() => removeSelected(s.id)} className="rounded px-1 text-text-muted hover:bg-error-light hover:text-error" title="Remove">×</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Add-products picker */}
            <button
              onClick={() => setPickerOpen((o) => !o)}
              className="mt-2 text-sm font-medium text-accent hover:underline"
            >
              {pickerOpen ? 'Hide product search' : '+ Add products'}
            </button>
            {pickerOpen && (
              <div className="mt-2 rounded-md border border-line p-2">
                <input
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Search products by title or SKU…"
                  className="w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                />
                <div className="mt-2 grid max-h-72 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                  {products.map((p) => {
                    const on = isSelected(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => toggleProduct(p)}
                        className={`group relative overflow-hidden rounded-md border text-left ${on ? 'border-accent ring-2 ring-accent/40' : 'border-line hover:border-accent/50'}`}
                      >
                        <div className="aspect-square w-full bg-surface-raised">
                          {p.image ? (
                            <img src={p.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                          ) : (
                            <div className="flex h-full items-center justify-center text-[10px] text-text-muted">No image</div>
                          )}
                        </div>
                        {on && (
                          <div className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs text-white">✓</div>
                        )}
                        <div className="truncate px-1.5 py-1 text-[11px] text-text-secondary">{p.title}</div>
                      </button>
                    );
                  })}
                  {products.length === 0 && !loadingProducts && (
                    <div className="col-span-full py-6 text-center text-xs text-text-muted">No products found.</div>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  {hasMore && (
                    <button
                      onClick={() => loadProducts({ reset: false })}
                      disabled={loadingProducts}
                      className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
                    >
                      {loadingProducts ? 'Loading…' : 'Load more'}
                    </button>
                  )}
                  {loadingProducts && !hasMore && <span className="text-xs text-text-muted">Loading…</span>}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-md border border-line bg-surface-raised/40 p-3">
            <label className="block text-sm font-medium text-text-primary">Collection copy → wholesale rewrite (AI)</label>
            <p className="text-xs text-text-muted">
              Paste the collection's copy or upload a .docx/.txt — AI condenses it into a short headline + intro
              below.
            </p>
            <textarea
              value={source}
              onChange={(e) => setSource(e.target.value)}
              rows={3}
              placeholder="Paste consumer / collection copy here…"
              className="mt-2 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
            <div className="mt-2 flex items-center gap-2">
              <label className="cursor-pointer rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface">
                {sourceFile ? sourceFile.name : 'Upload .docx/.txt'}
                <input
                  type="file"
                  accept=".docx,.txt,.md"
                  className="hidden"
                  onChange={(e) => setSourceFile(e.target.files?.[0] || null)}
                />
              </label>
              <button
                onClick={doRewrite}
                disabled={busy === 'rewrite' || (!source.trim() && !sourceFile)}
                className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
              >
                {busy === 'rewrite' ? 'Rewriting…' : 'Rewrite for wholesale'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary">Headline</label>
            <input
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="e.g. Autumn / Winter 26"
              className="mt-1.5 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary">Intro paragraph</label>
            <textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
              placeholder="Campaign intro in TEAMM8 brand voice…"
              className="mt-1.5 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          </div>
          <div className="rounded-md border border-line bg-surface-raised/40 p-3">
            <label className="block text-sm font-medium text-text-primary">Back-page contact</label>
            <select
              value={contactId}
              onChange={(e) => onContactPick(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            >
              <option value="">Choose a rep or CS member…</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.role === 'REP' ? 'Rep' : 'CS'}) · {c.email}
                </option>
              ))}
            </select>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Name" className="rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30" />
              <input value={contact.title} onChange={(e) => setContact({ ...contact, title: e.target.value })} placeholder="Title" className="rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30" />
              <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone" className="rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30" />
              <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email" className="rounded-md border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30" />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={doPreview}
              disabled={!canGenerate || busy === 'preview'}
              className="rounded-md border border-line px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
            >
              {busy === 'preview' ? 'Loading…' : 'Preview'}
            </button>
            <button
              onClick={doDownload}
              disabled={!canGenerate || busy === 'download'}
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {busy === 'download' ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        {/* Preview */}
        <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
          {!preview ? (
            <div className="flex h-full min-h-[200px] items-center justify-center text-center text-sm text-text-muted">
              Click <span className="mx-1 font-medium text-text-primary">Preview</span> to see the exact lookbook
              (the same PDF that downloads).
            </div>
          ) : (
            <div>
              <div className="mb-3 rounded-md bg-surface-raised/60 p-2 text-xs text-text-muted">
                {preview.summary.rendered}/{preview.summary.total} products rendered
                {preview.summary.noImage.length > 0 && ` · ${preview.summary.noImage.length} skipped (no image)`}
                {preview.summary.degraded.length > 0 && ` · ${preview.summary.degraded.length} with <4 images`}
              </div>
              {previewUrl && (
                <iframe
                  src={previewUrl}
                  title="Lookbook preview"
                  className="h-[80vh] w-full rounded-md border border-line bg-white"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Send */}
      {!embedded && (
      <div className="mt-4 rounded-lg border border-line bg-surface p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-text-primary">Send lookbook</h2>
        <p className="mt-0.5 text-xs text-text-muted">
          Pick clients to send to — or filter to a rep's clients and select the group. The client's assigned rep is
          always CC'd. Reps see only their assigned clients; staff send from their own Gmail, reps via the branded
          TEAMM8 sender (reply-to them).
        </p>
        {sendResult ? (
          <div className="mt-3 rounded-md bg-success-light px-3 py-2 text-sm text-success">
            Sent to {sendResult.recipients} recipient(s){sendResult.cc ? `, ${sendResult.cc} rep(s) CC'd` : ''} via {sendResult.channel}.{' '}
            <button onClick={() => { setSendResult(null); clearRecipients(); }} className="underline">Send another</button>
          </div>
        ) : (
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-md border border-line">
              <div className="flex flex-wrap items-center gap-2 border-b border-line p-2">
                <input
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                  placeholder="Search by name, email, company, or rep…"
                  className="min-w-0 flex-1 rounded-md border border-line px-3 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                />
                {reps.length > 0 && (
                  <select
                    value={repFilter}
                    onChange={(e) => setRepFilter(e.target.value)}
                    className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm outline-none focus:border-accent"
                    title="Filter to a rep's clients"
                  >
                    <option value="">All reps</option>
                    {reps.map((rp) => (
                      <option key={rp.id} value={rp.id}>{rp.name || rp.email}'s clients</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="flex items-center justify-between px-3 py-1.5 text-xs text-text-muted">
                <span>{recipientSel.size} selected · {filteredRecipients.length} shown</span>
                <span className="flex gap-2">
                  <button onClick={selectAllFiltered} className="text-accent hover:underline">Select shown</button>
                  {recipientSel.size > 0 && <button onClick={clearRecipients} className="text-error hover:underline">Clear</button>}
                </span>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {filteredRecipients.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-text-muted">
                    {recipientsAll.length === 0 ? 'No client logins available.' : 'No matches.'}
                  </div>
                ) : (
                  filteredRecipients.map((r) => (
                    <label key={r.email} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-surface-raised">
                      <input type="checkbox" checked={recipientSel.has(r.email)} onChange={() => toggleRecipient(r.email)} className="accent-accent" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-text-primary">
                          {r.name || r.email}
                          {r.primary && <span className="ml-1 text-[10px] text-accent" title="Primary contact">★</span>}
                        </span>
                        <span className="block truncate text-xs text-text-muted">
                          {r.company ? `${r.company} · ` : ''}{r.email}
                          {r.repNames?.length ? ` · rep: ${r.repNames.join(', ')}` : ''}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium text-text-primary">Email subject &amp; message</span>
                <button
                  type="button"
                  onClick={doDraftMessage}
                  disabled={busy === 'draftMsg' || selected.length === 0}
                  title="Draft the subject + a full email (styles, fabrication, colours) with AI"
                  className="shrink-0 rounded-md border border-accent px-2.5 py-1 text-xs font-medium text-accent hover:bg-accent-light disabled:opacity-50"
                >
                  {busy === 'draftMsg' ? 'Writing…' : '✨ Write with AI'}
                </button>
              </div>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject…"
                className="rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
              <textarea
                value={introMessage}
                onChange={(e) => setIntroMessage(e.target.value)}
                rows={9}
                placeholder="Email body (brand voice)… or click Write with AI to draft it from the collection's styles."
                className="flex-1 rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
            </div>
          </div>
        )}
        {!sendResult && !confirming && (
          <button
            onClick={() => setConfirming(true)}
            disabled={!canGenerate || recipientList.length === 0}
            className="mt-3 rounded-md border border-line px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50"
          >
            Review &amp; send
          </button>
        )}
        {confirming && (
          <div className="mt-3 rounded-md border border-line bg-surface-raised/50 p-3">
            <div className="text-sm text-text-primary">
              Send the <span className="font-medium">{title || preview?.collection || 'selected'}</span> lookbook to{' '}
              <span className="font-medium">{recipientList.length}</span> client(s)?
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {recipientList.map((r) => (
                <span key={r} className="rounded bg-surface px-1.5 py-0.5 text-xs text-text-secondary">
                  {r}
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setConfirming(false)} className="rounded-md border border-line px-3 py-1.5 text-sm text-text-secondary hover:bg-surface">
                Cancel
              </button>
              <button
                onClick={doSend}
                disabled={busy === 'send'}
                className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
              >
                {busy === 'send' ? 'Sending…' : 'Confirm & send'}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Send log */}
      {sends.length > 0 && (
        <div className="mt-4 rounded-lg border border-line bg-surface p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-text-primary">{embedded ? 'Earlier lookbook sends' : 'Send log'}</h2>
          <div className="space-y-1.5">
            {sends.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-1.5 text-xs last:border-0">
                <span className="font-medium text-text-primary">{s.collection}</span>
                <span className="text-text-muted">
                  {s.recipientCount} recipient(s) · {s.channel} · {s.status} · {s.by} ·{' '}
                  {new Date(s.at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
